import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { foyerApi } from "../http/foyer";
import { tokens } from "../theme";
import { EditDrawer, ItemFormSurface, type Dismiss } from "./EditDrawer";
import { describeError, useToast } from "./toast";
import { DAYS_OF_WEEK } from "@shared/types/foyer";
import type {
  DayOfWeek,
  PowerSchedule,
  PowerScheduleRule,
} from "@shared/types/foyer";
import { PermissionGate } from "../auth/access";
import { useFormat, type Formatters } from "../i18n/format";
import type { Translate } from "../i18n/translate";

interface Props {
  open: boolean;
  premiseId: string;
  onClose: () => void;
}

const DAY_OFFSET: Record<DayOfWeek, number> = {
  mon: 0,
  tue: 1,
  wed: 2,
  thu: 3,
  fri: 4,
  sat: 5,
  sun: 6,
};

function dayLabel(fmt: Formatters, d: DayOfWeek): string {
  return fmt.date(new Date(2024, 0, 1 + DAY_OFFSET[d]), { weekday: "short" });
}

const RULE_SEPARATOR = "  ·  ";

const BROWSER_TZ =
  typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    : "UTC";

function ruleSummary(
  t: Translate,
  fmt: Formatters,
  r: PowerScheduleRule
): string {
  const days =
    r.days.length === 7
      ? t("premises:powerScheduleDialog.everyDay")
      : r.days.map((d) => dayLabel(fmt, d)).join(" ");
  return t("premises:powerScheduleDialog.ruleSummary", {
    action: r.action === "on" ? t("common:labels.on") : t("common:labels.off"),
    time: r.time,
    days: days || t("premises:powerScheduleDialog.noDays"),
  });
}

function DayPicker({
  value,
  onChange,
}: {
  value: DayOfWeek[];
  onChange: (next: DayOfWeek[]) => void;
}) {
  const fmt = useFormat();
  return (
    <ToggleButtonGroup
      size="small"
      value={value}
      onChange={(_, next: DayOfWeek[]) => onChange(next)}
    >
      {DAYS_OF_WEEK.map((d) => (
        <ToggleButton key={d} value={d} sx={{ px: 1.25 }}>
          {dayLabel(fmt, d)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

interface DraftForm {
  name: string;
  timezone: string;
  rules: PowerScheduleRule[];
  enabled: boolean;
}

function emptyDraft(): DraftForm {
  return {
    name: "",
    timezone: BROWSER_TZ,
    rules: [
      { days: [...DAYS_OF_WEEK], time: "23:00", action: "off" },
      { days: [...DAYS_OF_WEEK], time: "07:00", action: "on" },
    ],
    enabled: true,
  };
}

export default function PowerScheduleDialog({
  open,
  premiseId,
  onClose,
}: Props) {
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();
  const [schedules, setSchedules] = useState<PowerSchedule[]>([]);
  const [error, setError] = useState<string | null>(null);
  // The schedule form: a modal for "New schedule" (id null), a drawer stacked
  // on the list for an existing one. `editing` is kept while the form slides
  // out (clearing it mid-slide would flip the drawer into the modal); it is
  // replaced when the next New / Edit is clicked.
  const [editing, setEditing] = useState<{
    id: string | null;
    draft: DraftForm;
  } | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // How many of this premise's displays can actually obey a schedule. A device
  // whose shell has no power backend silently ignores the `power` command, so
  // scheduling it would look configured while changing nothing (and earning no
  // energy saving). Counted here so the dialog says so up front.
  const [coverage, setCoverage] = useState<{
    total: number;
    controllable: number;
  } | null>(null);

  const load = useCallback(async () => {
    if (!premiseId) return;
    try {
      const res = await foyerApi.listPowerSchedules(premiseId);
      setSchedules(res.schedules);
      setError(null);
    } catch {
      setError(t("powerScheduleDialog.loadFailed"));
    }
    try {
      const fleet = await foyerApi.fleetDevices({ premiseId });
      setCoverage({
        total: fleet.devices.length,
        controllable: fleet.devices.filter((d) =>
          d.live?.capabilities?.includes("powerControl")
        ).length,
      });
    } catch {
      // Non-fatal: the schedules still load, we just can't show coverage.
      setCoverage(null);
    }
  }, [premiseId, t]);

  useEffect(() => {
    if (open) {
      setFormOpen(false);
      setEditing(null);
      void load();
    }
  }, [open, load]);

  function startCreate() {
    setFormError(null);
    setEditing({ id: null, draft: emptyDraft() });
    setFormOpen(true);
  }

  function startEdit(s: PowerSchedule) {
    setFormError(null);
    setFormOpen(true);
    setEditing({
      id: s.scheduleId,
      draft: {
        name: s.name,
        timezone: s.timezone,
        rules: s.rules.map((r) => ({ ...r })),
        enabled: s.enabled,
      },
    });
  }

  function setDraft(patch: Partial<DraftForm>) {
    setEditing((prev) =>
      prev ? { ...prev, draft: { ...prev.draft, ...patch } } : prev
    );
  }

  function setRule(i: number, patch: Partial<PowerScheduleRule>) {
    setEditing((prev) =>
      prev
        ? {
            ...prev,
            draft: {
              ...prev.draft,
              rules: prev.draft.rules.map((r, idx) =>
                idx === i ? { ...r, ...patch } : r
              ),
            },
          }
        : prev
    );
  }

  const save = useCallback(
    async (dismiss: Dismiss) => {
      if (!editing) return;
      const { id, draft } = editing;
      const payload = {
        name: draft.name.trim(),
        timezone: draft.timezone.trim() || "UTC",
        rules: draft.rules,
        enabled: draft.enabled,
      };
      setSaving(true);
      setFormError(null);
      try {
        if (id) {
          await foyerApi.updatePowerSchedule(premiseId, id, payload);
        } else {
          await foyerApi.createPowerSchedule(premiseId, payload);
        }
        // The form slides out, then its onClose closes it; the list reloads.
        dismiss();
        toast.success(
          id
            ? t("powerScheduleDialog.saved", { name: payload.name })
            : t("powerScheduleDialog.created", { name: payload.name })
        );
        await load();
      } catch (err) {
        setFormError(t("powerScheduleDialog.saveFailed"));
        toast.error(
          describeError(t, "savePowerSchedule", err, { name: payload.name })
        );
      } finally {
        setSaving(false);
      }
    },
    [editing, premiseId, load, toast, t]
  );

  async function toggleEnabled(s: PowerSchedule) {
    try {
      await foyerApi.updatePowerSchedule(premiseId, s.scheduleId, {
        enabled: !s.enabled,
      });
      toast.success(
        s.enabled
          ? t("powerScheduleDialog.switchedOff", { name: s.name })
          : t("powerScheduleDialog.switchedOn", { name: s.name })
      );
      await load();
    } catch (err) {
      setError(t("powerScheduleDialog.updateFailed"));
      toast.error(
        describeError(t, "switchPowerSchedule", err, { name: s.name })
      );
    }
  }

  async function remove(s: PowerSchedule) {
    if (
      !window.confirm(t("powerScheduleDialog.deleteConfirm", { name: s.name }))
    )
      return;
    try {
      await foyerApi.deletePowerSchedule(premiseId, s.scheduleId);
      toast.success(t("powerScheduleDialog.deleted", { name: s.name }));
      await load();
    } catch (err) {
      setError(t("powerScheduleDialog.deleteFailed"));
      toast.error(
        describeError(t, "deletePowerSchedule", err, { name: s.name })
      );
    }
  }

  const draftValid = editing != null && editing.draft.name.trim() !== "";

  // Shown on the list and again on the form (as the old single dialog did in
  // both of its views); the form copy gets its own test id.
  const coverageAlerts = (testId: string) => (
    <>
      {coverage && coverage.total > 0 && coverage.controllable === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid={testId}>
          {t("powerScheduleDialog.coverageNone", { count: coverage.total })}
        </Alert>
      )}
      {coverage &&
        coverage.controllable > 0 &&
        coverage.controllable < coverage.total && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid={testId}>
            {t("powerScheduleDialog.coveragePartial", {
              controllable: coverage.controllable,
              total: coverage.total,
              rest: coverage.total - coverage.controllable,
            })}
          </Alert>
        )}
    </>
  );

  return (
    <>
      {/* The premise's schedule list. Opening it is managing existing
          records, so it slides in from the right; New opens a modal on top of
          it and Edit stacks a second drawer. */}
      <EditDrawer
        open={open}
        onClose={onClose}
        title={t("powerScheduleDialog.title")}
        subtitle={t("powerScheduleDialog.subtitle")}
        actions={(dismiss) => (
          <>
            <Button onClick={() => dismiss()}>
              {t("common:buttons.close")}
            </Button>
            <PermissionGate
              resource="screens"
              level="edit"
              place={{ premiseId }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={startCreate}
              >
                {t("powerScheduleDialog.newSchedule")}
              </Button>
            </PermissionGate>
          </>
        )}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {coverageAlerts("power-coverage")}

        {schedules.length === 0 ? (
          /* ── Empty state ────────────────────────────────────────── */
          <Card sx={{ p: 5, textAlign: "center", color: tokens.inkMuted }}>
            <PowerSettingsNewIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography sx={{ mt: 1 }}>
              {t("powerScheduleDialog.emptyTitle")}
            </Typography>
            <Typography variant="body2">
              {t("powerScheduleDialog.emptyBody")}
            </Typography>
          </Card>
        ) : (
          /* ── List ───────────────────────────────────────────────── */
          <Stack spacing={1.5}>
            {schedules.map((s) => (
              <Card
                key={s.scheduleId}
                sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "fontWeightSemibold" }}
                    >
                      {s.name}
                    </Typography>
                    {!s.enabled && (
                      <Chip
                        label={t("powerScheduleDialog.disabled")}
                        size="small"
                        sx={{ bgcolor: tokens.rowAlt, color: tokens.inkMuted }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {s.timezone}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {s.rules
                      .map((r) => ruleSummary(t, fmt, r))
                      .join(RULE_SEPARATOR) || t("powerScheduleDialog.noRules")}
                  </Typography>
                </Box>
                <PermissionGate
                  resource="screens"
                  level="edit"
                  place={{ premiseId }}
                  deniedProps={{ disabled: true }}
                >
                  <Switch
                    checked={s.enabled}
                    onChange={() => toggleEnabled(s)}
                    inputProps={{
                      "aria-label": t("powerScheduleDialog.toggleEnabled"),
                    }}
                  />
                </PermissionGate>
                <PermissionGate
                  resource="screens"
                  level="edit"
                  place={{ premiseId }}
                >
                  <IconButton
                    aria-label={t("powerScheduleDialog.editSchedule")}
                    onClick={() => startEdit(s)}
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                </PermissionGate>
                <PermissionGate
                  resource="screens"
                  level="edit"
                  place={{ premiseId }}
                >
                  <IconButton
                    aria-label={t("powerScheduleDialog.deleteSchedule")}
                    onClick={() => remove(s)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </PermissionGate>
              </Card>
            ))}
          </Stack>
        )}
      </EditDrawer>

      {editing && (
        <ItemFormSurface
          mode={editing.id ? "edit" : "create"}
          open={formOpen}
          onClose={() => setFormOpen(false)}
          busy={saving}
          maxWidth="sm"
          title={
            editing.id
              ? t("powerScheduleDialog.editSchedule")
              : t("powerScheduleDialog.newSchedule")
          }
          subtitle={t("powerScheduleDialog.title")}
          footerTop={
            formError && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {formError}
              </Alert>
            )
          }
          actions={(dismiss) => (
            <>
              <Button onClick={() => dismiss()}>
                {t("common:buttons.cancel")}
              </Button>
              <PermissionGate
                resource="screens"
                level="edit"
                place={{ premiseId }}
                deniedProps={{ disabled: true }}
              >
                <Button
                  variant="contained"
                  disabled={!draftValid || saving}
                  onClick={() => void save(dismiss)}
                >
                  {t("common:buttons.save")}
                </Button>
              </PermissionGate>
            </>
          )}
        >
          {coverageAlerts("power-coverage-form")}
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label={t("common:labels.name")}
              value={editing.draft.name}
              onChange={(e) => setDraft({ name: e.target.value })}
              fullWidth
              autoFocus
            />
            <TextField
              label={t("powerScheduleDialog.timezone")}
              value={editing.draft.timezone}
              onChange={(e) => setDraft({ timezone: e.target.value })}
              helperText={t("powerScheduleDialog.timezoneHelp")}
              fullWidth
            />
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2">
                  {t("powerScheduleDialog.rules")}
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setDraft({
                      rules: [
                        ...editing.draft.rules,
                        {
                          days: [...DAYS_OF_WEEK],
                          time: "23:00",
                          action: "off",
                        },
                      ],
                    })
                  }
                >
                  {t("powerScheduleDialog.addRule")}
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                {editing.draft.rules.map((r, i) => (
                  <Card key={i} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 90 }}>
                        <Select
                          value={r.action}
                          onChange={(e) =>
                            setRule(i, {
                              action: e.target.value as "on" | "off",
                            })
                          }
                        >
                          <MenuItem value="off">
                            {t("common:labels.off")}
                          </MenuItem>
                          <MenuItem value="on">
                            {t("common:labels.on")}
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        type="time"
                        value={r.time}
                        onChange={(e) => setRule(i, { time: e.target.value })}
                        sx={{ width: 130 }}
                      />
                      <Box sx={{ flex: 1 }} />
                      <IconButton
                        size="small"
                        aria-label={t("powerScheduleDialog.removeRule")}
                        onClick={() =>
                          setDraft({
                            rules: editing.draft.rules.filter(
                              (_, idx) => idx !== i
                            ),
                          })
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <DayPicker
                        value={r.days}
                        onChange={(days) => setRule(i, { days })}
                      />
                    </Box>
                  </Card>
                ))}
              </Stack>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={editing.draft.enabled}
                  onChange={(e) => setDraft({ enabled: e.target.checked })}
                />
              }
              label={t("common:labels.enabled")}
            />
          </Stack>
        </ItemFormSurface>
      )}
    </>
  );
}
